import { useEffect, useState } from 'react';
import { LOCAL_TILE_ID, gridContainerClass } from './grid-layout';
import { VideoTile } from './video-tile';
import { useMicLevel } from '../transcription/mic-level';
import type { VisioParticipant } from '../video/video-service';

export interface LocalTileInfo {
  stream: MediaStream | null;
  micOn: boolean;
  cameraOn: boolean;
  screenSharing: boolean;
}

/**
 * Tuile qui détecte SON PROPRE niveau audio (`useMicLevel`, déjà utilisé pour le micro local en
 * retranscription — l'API Web Audio ne fait aucune distinction locale/distante, un flux WebRTC
 * distant s'analyse de la même façon) pour la surbrillance « orateur ». Un composant PAR
 * participant (clé = userId) : chaque instance a son propre appel de hook, stable au nombre de
 * participants — pas de hook conditionnel/en boucle.
 */
function SpeakingVideoTile({
  stream,
  ...rest
}: Omit<React.ComponentProps<typeof VideoTile>, 'speaking' | 'stream'> & {
  stream: MediaStream | null;
}) {
  const { isSpeaking } = useMicLevel(stream);
  return <VideoTile stream={stream} speaking={isSpeaking} {...rest} />;
}

/**
 * Grille de tuiles vidéo — de 1 (soi seul) à ~12 participants (plafond du modèle,
 * `packages/db/src/visio/room-capacity.ts`). Mobile-first (`grid-layout.ts`). Une tuile cliquée
 * passe en MISE EN AVANT (spotlight) : elle occupe l'espace principal, les autres deviennent une
 * bande de vignettes qui défile horizontalement au-dessus — généralise l'ancien
 * `focusedTile: 'local'|'remote'|null` du 1:1 à N participants (`LOCAL_TILE_ID` désigne « Toi »).
 */
export function ParticipantGrid({
  local,
  localIsHost = false,
  localLabel = 'Toi',
  participants,
}: {
  local: LocalTileInfo;
  /** Sommes-NOUS l'hôte ? Affiche le repère « Hôte » sur notre tuile. Les tuiles distantes
   * portent leur PROPRE `VisioParticipant.isHost` (déjà calculé par le moteur), sans qu'on ait
   * à le recalculer ici. */
  localIsHost?: boolean;
  localLabel?: string;
  participants: VisioParticipant[];
}) {
  const [focusedId, setFocusedId] = useState<string | null>(null);

  // Si le participant mis en avant quitte la salle, on retombe sur la grille plutôt que
  // d'agrandir une tuile vide.
  useEffect(() => {
    if (
      focusedId &&
      focusedId !== LOCAL_TILE_ID &&
      !participants.some((p) => p.userId === focusedId)
    ) {
      setFocusedId(null);
    }
  }, [focusedId, participants]);

  const focused =
    focusedId === LOCAL_TILE_ID
      ? { id: LOCAL_TILE_ID, isLocal: true as const }
      : focusedId
        ? {
            id: focusedId,
            isLocal: false as const,
            participant: participants.find((p) => p.userId === focusedId),
          }
        : null;

  function renderLocalTile(className?: string) {
    return (
      <SpeakingVideoTile
        stream={local.stream}
        label={localLabel}
        muted
        mirrored={!local.screenSharing}
        screenShare={local.screenSharing}
        cameraOn={local.cameraOn || local.screenSharing}
        micOn={local.micOn}
        badge={localIsHost ? 'Hôte' : undefined}
        onActivate={() => setFocusedId((cur) => (cur === LOCAL_TILE_ID ? null : LOCAL_TILE_ID))}
        activateLabel={
          focusedId === LOCAL_TILE_ID ? 'Revenir à la vue normale' : 'Mettre en avant ta vue'
        }
        className={className}
      />
    );
  }

  function renderRemoteTile(p: VisioParticipant, className?: string) {
    const isFocused = focusedId === p.userId;
    return (
      <SpeakingVideoTile
        key={p.userId}
        stream={p.stream}
        label={p.displayName || 'Participant'}
        screenShare={p.screenSharing}
        cameraOn={p.cameraOn || p.screenSharing}
        micOn={p.micOn}
        badge={p.isHost ? 'Hôte' : undefined}
        onActivate={() => setFocusedId((cur) => (cur === p.userId ? null : p.userId))}
        activateLabel={
          isFocused
            ? 'Revenir à la vue normale'
            : `Mettre en avant la vue de ${p.displayName || 'ce participant'}`
        }
        className={className}
      />
    );
  }

  if (!focused) {
    return (
      <div className={gridContainerClass(participants.length + 1)}>
        {participants.map((p) => renderRemoteTile(p))}
        {renderLocalTile()}
      </div>
    );
  }

  const strip = [
    !focused.isLocal ? { id: LOCAL_TILE_ID } : null,
    ...participants.filter((p) => p.userId !== focused.id).map((p) => ({ id: p.userId, p })),
  ].filter((x): x is { id: string; p?: VisioParticipant } => x !== null);

  return (
    <div className="space-y-2">
      {strip.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {strip.map((tile) => (
            <div key={tile.id} className="shrink-0">
              {tile.id === LOCAL_TILE_ID
                ? renderLocalTile('w-24 sm:w-32')
                : renderRemoteTile(tile.p!, 'w-24 sm:w-32')}
            </div>
          ))}
        </div>
      )}
      {focused.isLocal
        ? renderLocalTile()
        : focused.participant
          ? renderRemoteTile(focused.participant)
          : null}
    </div>
  );
}
